import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { enhancedApi } from '@/services/api/generated/filevineSyncService';
import { useGetMatterDetailsQuery } from '@/services/api/mattersService';

export const useFilevineIngestPrompt = (matterId: string) => {
  const { data: matter, isSuccess } = useGetMatterDetailsQuery(matterId, {
    skip: !matterId,
  });
  const [triggerIngestion] = enhancedApi.useFilevineIngestionTriggerMutation();

  const currentUrl = matter?.external_cms_url ?? null;

  const hasFetchedOnceRef = useRef(false);
  const prevUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isSuccess) return;

    const prev = prevUrlRef.current;
    const curr = currentUrl;

    // Trigger if:
    // 1. We've fetched at least once before
    // 2. Previous URL was null/empty
    // 3. Current URL is a Filevine URL
    const wasEmpty = !prev;
    const isNowFilevine = curr && curr.toLowerCase().includes('filevine');

    if (hasFetchedOnceRef.current && wasEmpty && isNowFilevine) {
      // Delay slightly to avoid showing at the same time as the "CMS URL updated" toast
      setTimeout(() => {
        toast('Filevine URL detected', {
          description: 'Would you like to ingest content from Filevine?',
          duration: 120000, // 2 minutes
          action: {
            label: 'Ingest from Filevine',
            onClick: () => {
              triggerIngestion({ matterId })
                .unwrap()
                .then(() => toast.success('Ingestion started!'))
                .catch(() => toast.error('Failed to start ingestion'));
            },
          },
        });
      }, 1500);
    }

    if (!hasFetchedOnceRef.current) {
      hasFetchedOnceRef.current = true;
    }
    prevUrlRef.current = curr;
  }, [isSuccess, currentUrl, matterId, triggerIngestion]);
};
